import { marked } from 'marked';
import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import AgentAPI from './agent-sdk/api';
import { AGENT_MESSAGE_DIRECTION, IAgentMessage } from './agent-sdk/message';
import { Bulletin, Editor, Header, Screen } from './components';
import AgentScreen from './components/AgentScreen';
import { getConfig } from './config';

marked.use({
  // 关闭删除线样式
  tokenizer: { del() {} },
  renderer: {
    link({ href, title, text }) {
      return `<a target="_blank" rel="noopener noreferrer" href="${href}" title="${title}">${text}</a>`;
    },
    code({ text, lang, escaped }): string {
      let result = '<pre style="white-space: pre-wrap;">';
      if (lang) {
        result += `<code class="language-${lang}">${text}</code>`;
      } else {
        result += `<code>${text}</code>`;
      }
      result += '</pre>';
      return result;
    },
  },
});
const agentAPI = new AgentAPI(getConfig().serviceURL + '/agent');
export default function AppAgent() {
  const [code, setCode] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [freeze, setFreeze] = React.useState<boolean>(false);
  const [messages, setMessages] = React.useState<IAgentMessage[]>([]);
  const [messageBufferContent, setMessageBufferContent] =
    React.useState<string>('');
  const [conversationID, setConversationID] = React.useState<string>('');
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const messageHello = {
    direction: AGENT_MESSAGE_DIRECTION.TO_ANONYMOUS,
    data: {
      message_id: 'hello',
      content: '您好，有什么需要我帮助的吗？',
    },
  };
  const messageBuffer = messageBufferContent
    ? {
        direction: AGENT_MESSAGE_DIRECTION.TO_ANONYMOUS,
        data: {
          message_id: 'buffer',
          content: messageBufferContent,
        },
      }
    : null;
  const messageLoading = loading
    ? {
        direction: AGENT_MESSAGE_DIRECTION.TO_ANONYMOUS,
        data: {
          message_id: 'loading',
          content:
            '<div class="flex items-center">' +
            '<span>思考中</span>' +
            '<img class="ml-1" src="/media/images/agent-thinking.svg" alt="thinking" style="width: 25px;height: 25px;" />' +
            '</div> ',
        },
      }
    : null;

  const handleSubmit = useCallback(
    async (code) => {
      if (loading || freeze) return;
      setLoading(true);
      setFreeze(true);
      setMessageBufferContent('');
      setMessages((messages) => [
        ...messages,
        {
          direction: AGENT_MESSAGE_DIRECTION.TO_AGENT,
          data: {
            message_id: uuidv4(),
            content: code,
          },
        },
      ]);
      const answerStream = await agentAPI.chatMessages({
        content: code,
        conversation_id: conversationID,
      });
      if (!answerStream) {
        setTimeout(() => handleSubmit(code), 1000);
        return;
      }
      const reader = answerStream.getReader();
      const decoder = new TextDecoder('utf-8');
      let answer = '';
      let buffer = '';
      const processChunkStr = async (chunkStr: string) => {
        if (chunkStr.startsWith('data:')) {
          const jsonData = chunkStr.replace('data:', '').trim();
          const data = JSON.parse(jsonData);
          if (data.event === 'message') {
            setLoading(false);
          }
          if (data.conversation_id) {
            setConversationID(data.conversation_id);
          }
          answer += data.answer || '';
          setMessageBufferContent(await marked.parse(answer, { breaks: true }));
        }
      };
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          // 还有未处理完的数据
          await processChunkStr(buffer);
          // 使用IIFE防止answer的循环变量问题
          void (async (answer) => {
            const content = await marked.parse(answer, { breaks: true });
            setMessages((messages) => [
              ...messages,
              {
                direction: AGENT_MESSAGE_DIRECTION.TO_ANONYMOUS,
                data: {
                  message_id: uuidv4(),
                  content,
                },
              },
            ]);
          })(answer);
          setFreeze(false);
          setMessageBufferContent('');
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        // 按行解析数据
        let lines = buffer.split('\n');
        buffer = lines.pop() || ''; // 可能有不完整的数据，留待下次处理
        for (let line of lines) {
          await processChunkStr(line);
        }
      }
    },
    [loading, freeze, conversationID]
  );

  return (
    <div id="root" className="h-screen flex flex-col justify-between">
      <Header
        logo={getConfig().header.logo}
        style={{ height: '7%', minHeight: '60px' }}
        serviceName={getConfig().header.content}
        schedule={queryParams.get('redirect') === '1' ? '西柚' : 'DeepSeek'}
      />

      <div className="flex divide-x" style={{ height: '93%' }}>
        <div className="flex-1 flex-1 flex flex-col bg-gray-50">
          <AgentScreen
            className="flex-1 scrolling-auto overflow-y-scroll py-2"
            messages={[
              messageHello,
              ...messages,
              ...(messageBuffer
                ? [messageBuffer]
                : messageLoading
                ? [messageLoading]
                : []),
            ]}
          />
          <div>
            <div
              className="flex items-center px-2 border-t border-gray-300 bg-gray-100 "
              style={{ height: '36px', fontSize: '14px' }}
            >
              以上内容由系统学习相关知识后生成，仅供参考
            </div>
            <Editor
              code={code}
              disabled={freeze}
              handleSubmitFile={() => {}}
              handleChange={(code) => setCode(code)}
              handleSubmit={handleSubmit}
            />
          </div>
        </div>
        <div className="bg-white w-60 border-l hidden lg:block xl:block relative">
          <Bulletin
            content={getConfig().bulletin.content}
            helper={getConfig().bulletin.helper}
            worktime={getConfig().bulletin.worktime}
          />
        </div>
      </div>
    </div>
  );
}
